/* eslint-disable no-use-before-define */
import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation } from '@apollo/client'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import Snackbar from '@material-ui/core/Snackbar'
import MuiAlert from '@material-ui/lab/Alert'
import Icon from '@material-ui/core/Icon'
import { ResetCacheDB, CacheKey } from '@services/app/mutation'
import appClient from '@services'
import { remove } from 'local-storage'
import { useUserDispatcher } from '@hooks'

import { IServiceResponse } from '@services/model'

import ConfirmationDialog from './localComponents/ConfirmationDialog'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'

function Alert(props: any) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    width: 350,
    marginLeft: 8,
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}))

const ClearCacheDB: React.FC = () => {
  const { clearUserAuthData } = useUserDispatcher()
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [openSnackBar, setOpenSnackbar] = useState(false)
  const [isCacheDBCleared, setIsCacheDBCleared] = useState(false)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
  const [cacheKey, setCacheKey] = useState('*')

  const [errorCacheKey, setErrorCacheKey] = useState(false)

  const [resetCacheDB] = useMutation(ResetCacheDB, { client: appClient })

  const goToLogin = () => {
    remove('token')
    clearUserAuthData()
  }

  const handleResetCacheDB = async () => {
    setOpenConfirmationDialog(false)

    try {
      setIsLoading(true)
      const resetCacheDBVariables = {
        isAllKeyReset: false,
        cacheKeyList: []
      }

      if (cacheKey == '*') {
        resetCacheDBVariables.isAllKeyReset = true;
      } else {
        resetCacheDBVariables.cacheKeyList.push(cacheKey)
      }

      const resetCacheDBResponse = await resetCacheDB({ variables: resetCacheDBVariables })

      const { result } = resetCacheDBResponse.data.resetCacheDB as IServiceResponse<boolean>
      setIsCacheDBCleared(result)

      setOpenSnackbar(true)
      if (result) {
        setCacheKey('*')
      }
    } catch (e) {
      if (e?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }

      console.error(e)
    }
    setIsLoading(false)
  }

  const handleOnCloseSnackbar = () => {
    setOpenSnackbar(false)
  }
  //Max chars for title and body and other data are 4000bytes, but to prevent issue we limit to 1024 for body
  //https://firebase.google.com/docs/cloud-messaging/concept-options
  return <div style={{
    border: '1px solid #dedede',
    padding: '20px',
    borderRadius: '5px',
    background: '#fff',
    width: '400px'
  }}>
    <h3>Reset Cache DB</h3>
    <Snackbar open={openSnackBar} onClose={handleOnCloseSnackbar} autoHideDuration={6000}>
      <Alert onClose={handleOnCloseSnackbar} severity={isCacheDBCleared ? 'success' : 'error'}>
        {isCacheDBCleared ? `Cache Key(s) has successfully cleared!` : 'Failed to clear the cache key(s)!'}
      </Alert>
    </Snackbar>
    {openConfirmationDialog && <ConfirmationDialog open={openConfirmationDialog} name={cacheKey == '*' ? 'all keys' : 'selected key'} handleYes={handleResetCacheDB} handleNo={() => setOpenConfirmationDialog(false)} />}
    <form className={classes.root} noValidate autoComplete="off">
      <FormControl fullWidth>
        <InputLabel htmlFor="cacheKey">Cache Key*</InputLabel>
        <Select
          native
          value={cacheKey}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setCacheKey(value)
            setErrorCacheKey(value === '')
          }}
          inputProps={{
            name: 'cacheKey',
            id: 'cacheKey',
          }}
          fullWidth
        >
          <option value="*">All</option>
          <option value={CacheKey.COURSES_KEY}>Course</option>
          <option value={CacheKey.SUPPORTMATERIALS_KEY}>Support Video</option>
        </Select>
        {errorCacheKey && <span style={{ color: 'red', fontSize: 12 }}>Cache Key is required</span>}
      </FormControl>

      <FormControl>
        <Button disabled={isLoading} variant="contained" startIcon={<Icon>send</Icon>} onClick={() => {
          const isCacheKeyEmpty = cacheKey === ''
          setErrorCacheKey(isCacheKeyEmpty)

          if (isCacheKeyEmpty) {
            return
          }

          setOpenConfirmationDialog(true)
        }} color="primary">
          Clear
        </Button>
      </FormControl>
    </form >
  </div >
}

export default ClearCacheDB